import { FormEvent, useState } from 'react';
import { api, DNS_RECORD_TYPES, DnsInfo, DnsRecord, DnsRecordType } from '../../api';
import { Alert, Loading, useAction, useLoad } from '../../Components/ui';
import type { TabProps } from './ControlPanel';

type Draft = { type: DnsRecordType; name: string; content: string; ttl: number; priority: number | '' };
const emptyDraft: Draft = { type: 'A', name: '@', content: '', ttl: 3600, priority: '' };
const hasPriority = (type: DnsRecordType) => type === 'MX' || type === 'SRV';

const placeholders: Record<DnsRecordType, string> = {
    A: '203.0.113.10', AAAA: '2001:db8::10', CNAME: 'target.example.com', MX: 'mail.example.com',
    TXT: 'v=spf1 include:example.com -all', NS: 'ns1.example.com', SRV: '10 5060 sip.example.com', CAA: '0 issue "letsencrypt.org"',
};

function toBody(d: Draft) {
    return { type: d.type, name: d.name, content: d.content, ttl: Number(d.ttl), priority: hasPriority(d.type) && d.priority !== '' ? Number(d.priority) : null };
}

function RecordForm({ initial, submitLabel, onSubmit, onCancel, busy }: {
    initial: Draft; submitLabel: string; busy: boolean; onSubmit: (d: Draft) => void; onCancel?: () => void;
}) {
    const [d, setD] = useState<Draft>(initial);
    const set = <K extends keyof Draft>(key: K, value: Draft[K]) => setD(prev => ({ ...prev, [key]: value }));

    return (
        <form className="fi-form" onSubmit={(e: FormEvent) => { e.preventDefault(); onSubmit(d); }}>
            <div className="inline-fields">
                <label>Type
                    <select value={d.type} onChange={e => set('type', e.target.value as DnsRecordType)}>
                        {DNS_RECORD_TYPES.map(t => <option key={t}>{t}</option>)}
                    </select>
                </label>
                <label>Name<input value={d.name} onChange={e => set('name', e.target.value)} required placeholder="@ or www" /></label>
                <label style={{ gridColumn: 'span 2' }}>Content<input value={d.content} onChange={e => set('content', e.target.value)} required placeholder={placeholders[d.type]} /></label>
                <label>TTL<input type="number" min={60} max={86400} value={d.ttl} onChange={e => set('ttl', Number(e.target.value))} required /></label>
                {hasPriority(d.type) && (
                    <label>Priority<input type="number" min={0} max={65535} value={d.priority} onChange={e => set('priority', e.target.value === '' ? '' : Number(e.target.value))} required /></label>
                )}
            </div>
            <div className="btn-row" style={{ marginTop: '1rem' }}>
                <button className="btn-submit inline small" disabled={busy}>{submitLabel}</button>
                {onCancel && <button type="button" className="btn-outline small" onClick={onCancel}>Cancel</button>}
            </div>
        </form>
    );
}

export default function DnsTab({ base, detail }: TabProps) {
    const { data: dns, error, loading, reload } = useLoad(
        () => api.get<{ dns: DnsInfo }>(`${base}/dns`).then(r => r.data.dns), [base]);
    const records = dns?.records;
    // Editing and deleting records is not in the CarbonEdge B2B API yet.
    const canEdit = detail.capabilities.dnsEdit;
    const [editing, setEditing] = useState<string | null>(null);
    const [adding, setAdding] = useState(false);
    const action = useAction();

    const create = (d: Draft) => action.run(async () => {
        await api.post(`${base}/dns/records`, toBody(d));
        setAdding(false);
        await reload();
    });
    const update = (id: string, d: Draft) => action.run(async () => {
        await api.put(`${base}/dns/records/${id}`, toBody(d));
        setEditing(null);
        await reload();
    });
    const remove = (r: DnsRecord) => {
        if (!window.confirm(`Delete ${r.type} record ${r.name}?`)) return;
        void action.run(async () => {
            await api.delete(`${base}/dns/records/${r.id}`);
            await reload();
        });
    };

    return (
        <div className="panel-card">
            <div className="panel-card-header">
                <h3>DNS records for {detail.service.domain}</h3>
                {!adding && <button className="btn-submit inline small" onClick={() => setAdding(true)}>Add record</button>}
            </div>
            {action.error && <Alert type="error">{action.error}</Alert>}
            {error && <Alert type="error">{error}</Alert>}
            {adding && (
                <div style={{ marginBottom: '1.5rem' }}>
                    <RecordForm initial={emptyDraft} submitLabel="Add record" busy={action.busy} onSubmit={create} onCancel={() => setAdding(false)} />
                </div>
            )}
            {loading && !records ? <Loading /> : (
                <div className="table-wrap">
                    <table className="data-table">
                        <thead><tr><th>Type</th><th>Name</th><th>Content</th><th>TTL</th><th>Prio</th><th /></tr></thead>
                        <tbody>
                            {records?.map(r => editing === r.id ? (
                                <tr key={r.id}><td colSpan={6}>
                                    <RecordForm
                                        initial={{ type: r.type, name: r.name, content: r.content, ttl: r.ttl, priority: r.priority ?? '' }}
                                        submitLabel="Save" busy={action.busy}
                                        onSubmit={d => update(r.id, d)} onCancel={() => setEditing(null)}
                                    />
                                </td></tr>
                            ) : (
                                <tr key={r.id}>
                                    <td><span className="tag tag-category">{r.type}</span></td>
                                    <td className="mono">{r.name}</td>
                                    <td className="mono">{r.content}</td>
                                    <td>{r.ttl}</td>
                                    <td>{r.priority ?? ''}</td>
                                    <td className="actions">
                                        {canEdit && (
                                            <>
                                                <button className="btn-outline small" onClick={() => setEditing(r.id)}>Edit</button>
                                                <button className="btn-outline small danger" onClick={() => remove(r)} disabled={action.busy}>Delete</button>
                                            </>
                                        )}
                                    </td>
                                </tr>
                            ))}
                            {records?.length === 0 && <tr><td colSpan={6} className="empty-state">No records yet.</td></tr>}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}
